import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Tooltip,
  Stack,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Badge
} from '@mui/material';
import {
  Notifications as AlertIcon,
  Check as ResolveIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Diamond as DiamondIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  PriorityHigh as PriorityHighIcon
} from '@mui/icons-material';
import { mockAlerts, mockAssets, mockUsers } from '../../mock/mockData';

const AlertManagement = () => {
  const [alerts, setAlerts] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'

  useEffect(() => {
    // Fetch alerts and add additional data like asset and user details
    const enhancedAlerts = mockAlerts.map(alert => {
      const asset = mockAssets.find(asset => asset.id === alert.assetId);
      const user = mockUsers.find(user => user.id === alert.userId);
      
      return {
        ...alert,
        assetName: asset ? asset.name : 'Unknown Asset',
        assetType: asset ? asset.type : 'Unknown',
        userName: user ? user.name : 'Unknown User',
        userEmail: user ? user.email : 'Unknown'
      };
    });
    
    // Sort by timestamp, newest first
    enhancedAlerts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    setAlerts(enhancedAlerts);
    setFilteredAlerts(enhancedAlerts);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, statusFilter, typeFilter, severityFilter, alerts]);

  const applyFilters = () => {
    let filtered = [...alerts];
    
    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(alert => 
        alert.assetName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alert.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alert.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alert.message.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(alert => alert.status === statusFilter);
    }
    
    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(alert => alert.type === typeFilter);
    }
    
    // Apply severity filter
    if (severityFilter !== 'all') {
      filtered = filtered.filter(alert => alert.severity === severityFilter);
    }
    
    setFilteredAlerts(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenViewDialog = (alert) => {
    setCurrentAlert(alert);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setCurrentAlert(null);
  };

  const handleResolveAlert = (alertId) => {
    const updatedAlerts = alerts.map(alert => 
      alert.id === alertId ? { ...alert, status: 'resolved', resolvedAt: new Date().toISOString() } : alert
    );
    
    setAlerts(updatedAlerts);
    
    if (currentAlert && currentAlert.id === alertId) {
      setCurrentAlert({ ...currentAlert, status: 'resolved', resolvedAt: new Date().toISOString() });
    }
    
    setSnackbar({
      open: true,
      message: 'Alert resolved successfully',
      severity: 'success'
    });
  };

  const handleDeleteAlert = (alertId) => {
    const updatedAlerts = alerts.filter(alert => alert.id !== alertId);
    setAlerts(updatedAlerts);
    
    if (openViewDialog && currentAlert && currentAlert.id === alertId) {
      handleCloseViewDialog();
    }
    
    setSnackbar({
      open: true,
      message: 'Alert deleted successfully',
      severity: 'success'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const getAlertSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
      case 'warning':
        return <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />;
      case 'info':
        return <InfoIcon fontSize="small" sx={{ color: 'info.main' }} />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const getAlertSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  const getAlertStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'error';
      case 'pending':
        return 'warning';
      case 'resolved':
        return 'success';
      default:
        return 'default';
    }
  };

  const getAlertTypeLabel = (type) => {
    switch (type) {
      case 'PANIC':
        return 'Panic';
      case 'geofence_exit':
        return 'Geofence Exit';
      case 'battery_low':
        return 'Battery Low';
      case 'tampering':
        return 'Tampering';
      case 'movement':
        return 'Movement';
      default:
        return type;
    }
  };

  // Get unique alert types for filter
  const alertTypes = ['all', ...new Set(alerts.map(alert => alert.type))];
  
  // Get unique alert severities for filter
  const alertSeverities = ['all', ...new Set(alerts.map(alert => alert.severity))];

  return (
    <Box>
      {/* Filter and Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search alerts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'gray.500' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                {alertTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type === 'all' ? 'All Types' : getAlertTypeLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity</InputLabel>
              <Select
                value={severityFilter}
                label="Severity"
                onChange={(e) => setSeverityFilter(e.target.value)}
              >
                {alertSeverities.map((severity) => (
                  <MenuItem key={severity} value={severity}>
                    {severity === 'all' ? 'All Severities' : severity.charAt(0).toUpperCase() + severity.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={viewMode === 'table' ? <FilterIcon /> : <FilterIcon />}
                onClick={() => setViewMode(viewMode === 'table' ? 'card' : 'table')}
              >
                {viewMode === 'table' ? 'Card View' : 'Table View'}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {filteredAlerts.length} alerts found
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip 
                  size="small" 
                  label={`${alerts.filter(a => a.status === 'active').length} Active`} 
                  color="error" 
                  variant="outlined" 
                />
                <Chip 
                  size="small" 
                  label={`${alerts.filter(a => a.status === 'pending').length} Pending`} 
                  color="warning" 
                  variant="outlined" 
                />
                <Chip 
                  size="small" 
                  label={`${alerts.filter(a => a.status === 'resolved').length} Resolved`} 
                  color="success" 
                  variant="outlined" 
                />
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table View */}
      {viewMode === 'table' && (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Alert</TableCell>
                  <TableCell>Asset</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAlerts
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((alert) => (
                    <TableRow key={alert.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, width: 32, height: 32, bgcolor: `${getAlertSeverityColor(alert.severity)}.main` }}>
                            {getAlertSeverityIcon(alert.severity)}
                          </Avatar>
                          <Typography variant="body2">{alert.message}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DiamondIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2">{alert.assetName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">{alert.userName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getAlertTypeLabel(alert.type)}
                          color={alert.type === 'PANIC' ? 'error' : 'primary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={getAlertSeverityIcon(alert.severity)}
                          label={alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
                          color={getAlertSeverityColor(alert.severity)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
                          color={getAlertStatusColor(alert.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={new Date(alert.timestamp).toLocaleString()}>
                          <Typography variant="body2">
                            {new Date(alert.timestamp).toLocaleTimeString()}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleOpenViewDialog(alert)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {alert.status !== 'resolved' && (
                            <Tooltip title="Resolve Alert">
                              <IconButton 
                                size="small" 
                                color="success" 
                                onClick={() => handleResolveAlert(alert.id)}
                              >
                                <ResolveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete Alert">
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDeleteAlert(alert.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredAlerts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No alerts found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredAlerts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Card View */}
      {viewMode === 'card' && (
        <>
          <Grid container spacing={2}>
            {filteredAlerts
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((alert) => (
                <Grid item xs={12} sm={6} md={4} key={alert.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      borderLeft: 4,
                      borderColor: `${getAlertSeverityColor(alert.severity)}.main`,
                      boxShadow: alert.status === 'active' ? 3 : 1
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Chip
                          size="small"
                          label={getAlertTypeLabel(alert.type)}
                          color={alert.type === 'PANIC' ? 'error' : 'primary'}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
                          color={getAlertStatusColor(alert.status)}
                          variant="outlined"
                        />
                      </Box>
                      
                      <Typography variant="h6" gutterBottom>
                        {alert.message}
                      </Typography>
                      
                      <Stack spacing={1} sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DiamondIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2">Asset: {alert.assetName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">User: {alert.userName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {new Date(alert.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                        {alert.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {alert.location.address || `${alert.location.lat}, ${alert.location.lng}`}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                    <Divider />
                    <CardActions>
                      <Button 
                        size="small" 
                        startIcon={<ViewIcon />}
                        onClick={() => handleOpenViewDialog(alert)}
                      >
                        View
                      </Button>
                      {alert.status !== 'resolved' && (
                        <Button 
                          size="small" 
                          color="success" 
                          startIcon={<ResolveIcon />}
                          onClick={() => handleResolveAlert(alert.id)}
                        >
                          Resolve
                        </Button>
                      )}
                      <Button 
                        size="small" 
                        color="error" 
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteAlert(alert.id)}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
          </Grid>
          
          {filteredAlerts.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No alerts found
              </Typography>
            </Paper>
          )}
          
          <Box sx={{ mt: 2 }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredAlerts.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </>
      )}

      {/* View Alert Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ mr: 2, bgcolor: currentAlert ? `${getAlertSeverityColor(currentAlert.severity)}.main` : 'primary.main' }}>
              {currentAlert && getAlertSeverityIcon(currentAlert.severity)}
            </Avatar>
            <Typography variant="h6">
              Alert Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {currentAlert && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>Alert Information</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Message</Typography>
                          <Typography variant="body1">{currentAlert.message}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                          <Chip
                            label={getAlertTypeLabel(currentAlert.type)}
                            color={currentAlert.type === 'PANIC' ? 'error' : 'primary'}
                          />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Severity</Typography>
                          <Chip
                            icon={getAlertSeverityIcon(currentAlert.severity)}
                            label={currentAlert.severity.charAt(0).toUpperCase() + currentAlert.severity.slice(1)}
                            color={getAlertSeverityColor(currentAlert.severity)}
                          />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                          <Chip
                            label={currentAlert.status.charAt(0).toUpperCase() + currentAlert.status.slice(1)}
                            color={getAlertStatusColor(currentAlert.status)}
                          />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                          <Typography variant="body1">
                            {new Date(currentAlert.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                        {currentAlert.resolvedAt && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">Resolved At</Typography>
                            <Typography variant="body1">
                              {new Date(currentAlert.resolvedAt).toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>Related Information</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Asset</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                              <DiamondIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="body1">{currentAlert.assetName}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Type: {currentAlert.assetType}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">User</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <Avatar sx={{ mr: 2, bgcolor: 'success.main' }}>
                              {currentAlert.userName.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body1">{currentAlert.userName}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {currentAlert.userEmail}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        {currentAlert.location && (
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                              <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body1">
                                {currentAlert.location.address || `${currentAlert.location.lat}, ${currentAlert.location.lng}`}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>
                {currentAlert.details && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>Additional Details</Typography>
                      <Typography variant="body1">
                        {currentAlert.details}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          {currentAlert && currentAlert.status !== 'resolved' && (
            <Button 
              onClick={() => {
                handleResolveAlert(currentAlert.id);
                handleCloseViewDialog();
              }} 
              color="success" 
              variant="contained"
              startIcon={<ResolveIcon />}
            >
              Resolve Alert
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertManagement;
